import React, {
  FunctionComponent,
  KeyboardEvent,
  ReactNode,
  RefObject,
  useEffect,
  useId,
  useRef,
} from 'react';
import styles from './PopoverAnchor.module.scss';

export interface PopoverTriggerProps {
  ref: RefObject<HTMLButtonElement>;
  'aria-haspopup': 'menu' | 'listbox';
  'aria-expanded': boolean;
  'aria-controls': string;
  onClick: () => void;
}

interface PopoverAnchorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `menu` nadaje panelowi `role="menu"`; `listbox` zostawia rolę wewnętrznej liście. */
  popupType: 'menu' | 'listbox';
  /** `stretch` = na szerokość wyzwalacza (obszar roboczy), `end` = do prawej krawędzi. */
  align: 'stretch' | 'end';
  /** Nazwa panelu (tylko dla `role="menu"`). */
  label?: string;
  anchorClassName?: string;
  renderTrigger: (props: PopoverTriggerProps) => ReactNode;
  children: ReactNode;
}

/** Elementy, między którymi poruszają strzałki; wyszukiwarka obszaru liczy się jak pozycja. */
const NAVIGABLE = 'input, [data-popover-item]';

/**
 * Wyzwalacz + wysuwany panel (obszar roboczy, język, konto). Stan otwarcia trzyma rodzic, bo to
 * on pilnuje, żeby naraz był otwarty jeden panel. Tu: Escape i klik obok zamykają, strzałki
 * przechodzą po pozycjach, fokus trafia do panelu przy otwarciu i wraca na wyzwalacz przy zamknięciu.
 */
const PopoverAnchor: FunctionComponent<PopoverAnchorProps> = ({
  open,
  onOpenChange,
  popupType,
  align,
  label,
  anchorClassName,
  renderTrigger,
  children,
}) => {
  const popoverId = useId();
  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open) {
      popoverRef.current?.querySelector<HTMLElement>(NAVIGABLE)?.focus();
    } else if (wasOpen.current) {
      const active = document.activeElement;
      // fokus zniknął razem z panelem albo został w nim: oddajemy go wyzwalaczowi
      if (!active || active === document.body || anchorRef.current?.contains(active)) {
        triggerRef.current?.focus();
      }
    }
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event: MouseEvent) => {
      if (anchorRef.current && !anchorRef.current.contains(event.target as Node)) {
        onOpenChange(false);
      }
    };
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onOpenChange]);

  const onPopoverKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const items = Array.from(popoverRef.current?.querySelectorAll<HTMLElement>(NAVIGABLE) ?? []);
    if (items.length === 0) return;
    event.preventDefault();
    const index = items.indexOf(document.activeElement as HTMLElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    items[(index + step + items.length) % items.length].focus();
  };

  const popoverClassName = `${styles.popover} ${align === 'stretch' ? styles.stretch : styles.end}`;

  return (
    <div className={`${styles.anchor} ${anchorClassName ?? ''}`} ref={anchorRef}>
      {renderTrigger({
        ref: triggerRef,
        'aria-haspopup': popupType,
        'aria-expanded': open,
        'aria-controls': popoverId,
        onClick: () => onOpenChange(!open),
      })}
      {open && (
        <div
          id={popoverId}
          ref={popoverRef}
          className={popoverClassName}
          role={popupType === 'menu' ? 'menu' : undefined}
          aria-label={popupType === 'menu' ? label : undefined}
          onKeyDown={onPopoverKeyDown}
        >
          {children}
        </div>
      )}
    </div>
  );
};

export default PopoverAnchor;
